import { useEffect, useRef, useState } from "react";
import { Clapperboard, Loader2, Download, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";

const sel = "h-9 rounded-md bg-slate-900 border border-slate-700 px-2 text-sm";
const dl = "inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 px-3 py-1.5 text-xs text-amber-100 hover:bg-amber-600 transition-colors disabled:opacity-60";

function TrailerDownloads({ book }) {
  const [busy, setBusy] = useState(false);
  const vertical = async () => {
    setBusy(true);
    try { await api.post(`/books/${book.id}/trailer/vertical`); window.location.href = `${API}/books/${book.id}/trailer/download?format=vertical`; } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-1.5 pt-1" data-testid="trailer-downloads">
      <p className="text-xs text-slate-400">Download to post</p>
      <div className="flex flex-wrap gap-2">
        <a href={`${API}/books/${book.id}/trailer/download?format=original`} className={dl} data-testid="trailer-download-original"><Download size={12} />Original (2:3)</a>
        <button onClick={vertical} disabled={busy} className={dl} data-testid="trailer-download-vertical">{busy ? <Loader2 size={12} className="animate-spin" /> : <Smartphone size={12} />}{busy ? "Preparing…" : "Vertical 9:16"}</button>
      </div>
      <p className="text-[11px] text-slate-500">Vertical fits Instagram Reels, TikTok and YouTube Shorts.</p>
    </div>
  );
}

export default function TrailerPanel({ book, hasCover }) {
  const [d, setD] = useState(null);
  const [opt, setOpt] = useState({ duration: "5", sound: true, mood: "" });
  const [v, setV] = useState(0);
  const timer = useRef();
  const load = () => api.get(`/books/${book.id}/trailer`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); return () => clearInterval(timer.current); }, [book.id]); // eslint-disable-line
  const busy = ["queued", "running"].includes(d?.job?.status);
  useEffect(() => {
    clearInterval(timer.current);
    if (busy) timer.current = setInterval(async () => { const r = await api.get(`/books/${book.id}/trailer`); setD(r.data); if (r.data.job?.status === "done") { setV((x) => x + 1); toast.success("Trailer ready"); } }, 5000);
    return () => clearInterval(timer.current);
  }, [busy, book.id]);
  const start = async () => { try { const r = await api.post(`/books/${book.id}/trailer`, opt); setD({ ...d, job: r.data }); } catch (e) { toast.error(errMsg(e)); } };
  const vis = async (p) => { try { await api.put(`/books/${book.id}/trailer`, { public: p }); setD({ ...d, trailer: { ...d.trailer, public: p } }); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return null;
  const j = d.job;
  return (
    <div className="panel p-6 space-y-4" data-testid="trailer-panel">
      <div><p className="eyebrow flex items-center gap-2"><Clapperboard size={13} />Book trailer</p>
        <p className="text-sm text-slate-400 mt-1">An AI video that brings your showcase cover to life, guided by your blurb. Uses Universal Key credits (roughly $1 per 5 seconds) and takes a few minutes.</p></div>
      {d.trailer && (
        <div className="flex flex-wrap gap-4 items-start">
          <video key={v} src={`${API}/books/${book.id}/trailer/video?v=${v}`} controls playsInline preload="metadata" className="w-40 rounded-md border border-slate-700 bg-black" data-testid="trailer-preview-video" />
          <div className="space-y-2 text-sm">
            <p className="text-slate-400">{d.trailer.duration}s · {d.trailer.sound ? "with sound" : "silent"} · {(d.trailer.size / 1e6).toFixed(1)} MB</p>
            <label className="flex items-center gap-2"><Switch checked={!!d.trailer.public} onCheckedChange={vis} data-testid="trailer-public-switch" /><span>Show on the public book page</span></label>
            <TrailerDownloads book={book} />
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-center">
        <select value={opt.duration} onChange={(e) => setOpt({ ...opt, duration: e.target.value })} className={sel} data-testid="trailer-duration-select"><option value="5">5 seconds</option><option value="10">10 seconds</option></select>
        <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={opt.sound} onCheckedChange={(s) => setOpt({ ...opt, sound: s })} data-testid="trailer-sound-switch" />Music & ambience</label>
        <Input value={opt.mood} onChange={(e) => setOpt({ ...opt, mood: e.target.value })} placeholder="Optional mood, e.g. haunting, stormy night" className="h-9 flex-1 min-w-[180px] text-sm" maxLength={200} data-testid="trailer-mood-input" />
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <Button onClick={start} disabled={busy} className="bg-amber-600 hover:bg-amber-500 glow-btn" data-testid="trailer-generate-button">
          {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Clapperboard size={14} className="mr-1" />}{busy ? "Making trailer…" : d.trailer ? "Make a new trailer" : "Make trailer"}</Button>
        {busy && <span className="text-xs text-slate-400" data-testid="trailer-stage">{j.stage}</span>}
        {["failed", "interrupted"].includes(j?.status) && <span className="text-xs text-red-400" data-testid="trailer-error">{j.error}</span>}
      </div>
      {!hasCover && <p className="text-[11px] text-amber-500">Pick and save a cover image above first.</p>}
    </div>
  );
}
